import { ShieldAlert } from 'lucide-react'
import type { UseFormReturn } from 'react-hook-form'
import { FormInput, TimePicker } from '../../../../components'
import type { AttendanceSettingsFormValues } from '../types/hrmsAttendanceSettings.types'

interface AttendanceRulesCardProps {
  form: UseFormReturn<AttendanceSettingsFormValues>
  isEditing: boolean
  onToggleEdit: () => void
  isReadOnly?: boolean
}

export function AttendanceRulesCard({
  form,
  isEditing,
  onToggleEdit,
  isReadOnly = false,
}: AttendanceRulesCardProps) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = form

  const grace = watch('gracePeriodMinutes')
  const late = watch('lateThresholdMinutes')
  const halfDay = watch('halfDayHours')
  const cutoff = watch('absentCutoff')

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 min-w-0 font-sans">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-slate-700 shrink-0">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900 truncate">Attendance Rules</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Thresholds for lateness, half-days, and automated deductions.
            </p>
          </div>
        </div>

        {!isReadOnly && (
          <button
            type="button"
            onClick={onToggleEdit}
            className="text-xs font-semibold text-[#146971] hover:underline whitespace-nowrap shrink-0 pt-1 cursor-pointer"
          >
            {isEditing ? 'Done Editing' : 'Edit Rules'}
          </button>
        )}
      </div>

      {/* Body */}
      {isEditing && !isReadOnly ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
          <div>
            <FormInput
              label="Grace Period (Minutes)"
              type="number"
              min={0}
              {...register('gracePeriodMinutes', { valueAsNumber: true })}
              error={errors.gracePeriodMinutes?.message}
            />
            <p className="text-xs text-slate-500 mt-1">
              Arrival within this window is marked as Present without penalty.
            </p>
          </div>

          <div>
            <FormInput
              label="Late Threshold (Minutes)"
              type="number"
              min={0}
              {...register('lateThresholdMinutes', { valueAsNumber: true })}
              error={errors.lateThresholdMinutes?.message}
            />
            <p className="text-xs text-slate-500 mt-1">
              Arrival after grace period up to this threshold is marked as Late.
            </p>
          </div>

          <div>
            <FormInput
              label="Minimum Hours for Half-Day"
              type="number"
              step={0.5}
              min={0}
              {...register('halfDayHours', { valueAsNumber: true })}
              error={errors.halfDayHours?.message}
            />
            <p className="text-xs text-slate-500 mt-1">
              Working less than this duration counts as Half-Day attendance.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Absent Cutoff Time
            </label>
            <TimePicker
              value={cutoff}
              onChange={(val) => setValue('absentCutoff', val, { shouldValidate: true, shouldDirty: true })}
            />
            {errors.absentCutoff?.message && (
              <p className="text-xs text-red-500 mt-1">{errors.absentCutoff.message}</p>
            )}
            <p className="text-xs text-slate-500 mt-1">
              Staff with no punch by this time are automatically flagged as Absent.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3 pt-1">
          {/* Row 1: Late Arrival Threshold */}
          <div className="rounded-xl bg-gray-50 p-4 flex justify-between items-center gap-3 border border-slate-200/60">
            <div className="min-w-0">
              <h4 className="text-sm font-bold text-slate-900">Late Arrival Threshold</h4>
              <p className="text-xs text-slate-500 mt-0.5">Grace window: {grace ?? 0}m, Late trigger after grace</p>
            </div>
            <span className="bg-gray-200 rounded-lg px-3 py-1 text-sm font-semibold text-gray-700 whitespace-nowrap shrink-0">
              {late ?? 10} minutes
            </span>
          </div>

          {/* Row 2: Half-Day Threshold */}
          <div className="rounded-xl bg-gray-50 p-4 flex justify-between items-center gap-3 border border-slate-200/60">
            <div className="min-w-0">
              <h4 className="text-sm font-bold text-slate-900">Half-Day Threshold</h4>
              <p className="text-xs text-slate-500 mt-0.5">Cumulative logged duration below threshold</p>
            </div>
            <span className="bg-gray-200 rounded-lg px-3 py-1 text-sm font-semibold text-gray-700 whitespace-nowrap shrink-0">
              &lt; {halfDay ?? 4} hours
            </span>
          </div>

          {/* Row 3: Default Absent Classification */}
          <div className="rounded-xl bg-gray-50 p-4 flex justify-between items-center gap-3 border border-slate-200/60">
            <div className="min-w-0">
              <h4 className="text-sm font-bold text-slate-900">Default Absent Classification</h4>
              <p className="text-xs text-slate-500 mt-0.5">Condition for automatic absent flag trigger</p>
            </div>
            <span className="bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-1 text-sm font-semibold whitespace-nowrap shrink-0">
              No record by {cutoff || '19:00'}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
